/**
 * Linked Products Component
 *
 * Automatically displays product sibling swatches based on product naming conventions.
 * Products are linked by matching base titles (e.g., "T-Shirt - Black" and "T-Shirt - Navy")
 *
 * Usage:
 * <div data-component="linked" data-title="Product Name - Color" data-max-items="5"></div>
 */

class LinkedProductsComponent {
  // Shared fetch promise across all instances to prevent duplicate requests
  static fetchPromise = null;
  static productsCache = null;

  constructor(element) {
    this.element = element;
    this.productTitle = element.dataset.title || "";
    this.maxItems = parseInt(element.dataset.maxItems || "5", 10);
    this.size = element.dataset.size || "sm"; // 'sm' or 'base'
    this.products = [];

    this.config = {
      cacheKey: "linked-products",
      cacheExpiry: 1000 * 60 * 60, // 1 hour
      apiEndpoint: "/collections/all",
      colorTagPrefix: "color_",
      titleSeparator: "-",
      maxPages: 20, // Safety limit
    };
  }

  async init() {
    try {
      this.products = await this.fetchProducts();
      const siblings = this.findSiblings();

      if (siblings.length > 0) {
        this.render(siblings);
      }
    } catch (error) {
      console.error("[LinkedProducts] Failed to initialize", error);
      // Fail silently - don't break the page
    }
  }

  /**
   * Fetch all products from the API with caching
   * Uses static properties to ensure only ONE fetch happens across all instances
   */
  async fetchProducts() {
    // Check static in-memory cache first (fastest)
    if (LinkedProductsComponent.productsCache && LinkedProductsComponent.productsCache.length > 0) {
      return LinkedProductsComponent.productsCache;
    }

    // Check sessionStorage cache - only use if not empty
    const cached = this.getCache();
    if (cached && cached.length > 0) {
      LinkedProductsComponent.productsCache = cached;
      return cached;
    }

    // If a fetch is already in progress, wait for it
    if (LinkedProductsComponent.fetchPromise) {
      return await LinkedProductsComponent.fetchPromise;
    }

    // Start new fetch and store promise so other instances can wait
    LinkedProductsComponent.fetchPromise = this.fetchAllPages();

    try {
      const products = await LinkedProductsComponent.fetchPromise;
      // Only cache non-empty results
      if (products && products.length > 0) {
        this.setCache(products);
        LinkedProductsComponent.productsCache = products;
      }
      return products;
    } finally {
      // Clear the promise so future page loads can fetch again if needed
      LinkedProductsComponent.fetchPromise = null;
    }
  }

  /**
   * Fetch all paginated product data
   */
  async fetchAllPages() {
    const allProducts = [];
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= this.config.maxPages) {
      try {
        const response = await fetch(
          `${this.config.apiEndpoint}?view=linked&page=${page}`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const products = await response.json();

        if (products.length === 0) {
          hasMore = false;
        } else {
          allProducts.push(...products);
          page++;
        }
      } catch (error) {
        console.error(`[LinkedProducts] Failed to fetch page ${page}`, error);
        hasMore = false;
      }
    }

    return allProducts;
  }

  /**
   * Find sibling products by matching base title
   */
  findSiblings() {
    const baseTitle = this.extractBaseTitle(this.productTitle);

    const siblings = this.products.filter((product) => {
      const productBaseTitle = this.extractBaseTitle(product.title);
      return productBaseTitle.toLowerCase() === baseTitle.toLowerCase();
    });

    // Sort: current product first, then alphabetically
    siblings.sort((a, b) => {
      if (a.title === this.productTitle) return -1;
      if (b.title === this.productTitle) return 1;
      return a.title.localeCompare(b.title);
    });

    return siblings;
  }

  /**
   * Extract base title by removing color suffix
   * "T-Shirt - Black" -> "T-Shirt"
   */
  extractBaseTitle(title) {
    const parts = title.split(this.config.titleSeparator);

    if (parts.length > 1) {
      // Remove last part (assumed to be color)
      return parts.slice(0, -1).join(this.config.titleSeparator).trim();
    }

    return title.trim();
  }

  /**
   * Extract color from product title or tags (fallback)
   */
  extractColor(product) {
    // Method 1: Extract from title suffix (primary method)
    const parts = product.title.split(this.config.titleSeparator);
    if (parts.length > 1) {
      return parts[parts.length - 1].trim();
    }

    // Method 2: Check for color tag (fallback if title doesn't have separator)
    const colorTag = product.tags?.find((tag) =>
      tag.toLowerCase().startsWith(this.config.colorTagPrefix)
    );

    if (colorTag) {
      return colorTag.replace(
        new RegExp(`^${this.config.colorTagPrefix}`, "i"),
        ""
      );
    }

    return "";
  }

  /**
   * Convert color name to URL-safe slug
   * Matches Shopify Files naming convention
   */
  slugify(text) {
    return text
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (é→e, ñ→n, etc.)
      .toLowerCase()
      .replace(/\//g, "-") // Convert slashes to hyphens (e.g., "Green/Red" -> "green-red")
      .replace(/[^a-z0-9\s-]/g, "") // Remove remaining special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single
      .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
  }

  /**
   * Escape HTML to prevent XSS attacks
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get color swatch image URL from Shopify Files
   * Uses the same pattern as legacy theme: replaces 'x.x' with slugified color name
   */
  getSwatchImageUrl(color) {
    const slug = this.slugify(color);

    // Use window.theme.file_url pattern (set in head.liquid)
    // Example: "https://cdn.shopify.com/.../x.x_16x16_crop_center.png"
    // Replace "x.x" with slugified color name like "blues", "black", etc.
    if (window.theme && window.theme.file_url) {
      return window.theme.file_url.replace("x.x", slug);
    }

    // Fallback: use product featured image if file_url not available
    return "";
  }

  /**
   * Render swatches
   */
  render(siblings) {
    const swatchesHtml = [];
    const displayLimit = Math.min(siblings.length, this.maxItems);

    // Add "+" indicator at the beginning if there are more siblings
    const remaining = siblings.length - displayLimit;
    if (remaining > 0) {
      swatchesHtml.push(`
        <span class="linked-swatch__more">+</span>
      `);
    }

    for (let i = 0; i < displayLimit; i++) {
      const sibling = siblings[i];
      const color = this.extractColor(sibling);
      const isActive = sibling.title === this.productTitle;
      const sizeClass = this.size === "base" ? "w-4 h-4" : "w-3 h-3";
      const swatchImageUrl = this.getSwatchImageUrl(color);

      // Escape all user-controlled data to prevent XSS
      const escapedUrl = this.escapeHtml(sibling.url || "");
      const escapedColor = this.escapeHtml(color);
      const escapedSwatchUrl = this.escapeHtml(swatchImageUrl);

      swatchesHtml.push(`
        <div class="linked-swatch-wrapper">
          <div class="linked-swatch ${
            isActive ? "linked-swatch--active" : ""
          } ${sizeClass}">
            <a href="${escapedUrl}"
               title="${escapedColor}"
               data-color="${escapedColor}"
               class="linked-swatch__link">
              <img
                src="${escapedSwatchUrl}"
                alt="${escapedColor}"
                loading="lazy"
                class="linked-swatch__image"
                onerror="this.style.display='none'"
              >
            </a>
          </div>
          <div class="linked-swatch__underline ${
            isActive ? "linked-swatch__underline--active" : ""
          }"></div>
        </div>
      `);
    }

    this.element.innerHTML = swatchesHtml.join("");
    this.attachHoverEvents();
  }

  /**
   * Attach hover events for color name display
   */
  attachHoverEvents() {
    const colorDisplay = document.querySelector("[data-color-current]");
    if (!colorDisplay) return;

    const links = this.element.querySelectorAll("a[data-color]");
    const originalText = colorDisplay.textContent;

    links.forEach((link) => {
      link.addEventListener("mouseenter", () => {
        colorDisplay.textContent = link.dataset.color;
      });

      link.addEventListener("mouseleave", () => {
        colorDisplay.textContent = originalText;
      });
    });
  }

  /**
   * Cache management
   */
  getCache() {
    try {
      const cached = sessionStorage.getItem(this.config.cacheKey);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      if (age > this.config.cacheExpiry) {
        sessionStorage.removeItem(this.config.cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  }

  setCache(data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(this.config.cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("LinkedProducts: Failed to cache data", error);
    }
  }
}

/**
 * Initialize all linked product components on the page
 */
async function initLinkedProducts() {
  const elements = document.querySelectorAll('[data-component="linked"]');

  if (elements.length === 0) return;

  // Initialize all components
  const components = Array.from(elements).map(
    (el) => new LinkedProductsComponent(el)
  );

  // Wait for all to initialize
  await Promise.all(components.map((c) => c.init()));
}

// Auto-initialize on DOMContentLoaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initLinkedProducts);
} else {
  initLinkedProducts();
}

// Export for manual initialization if needed
window.LinkedProductsComponent = LinkedProductsComponent;
window.initLinkedProducts = initLinkedProducts;
